// src/types/interfaces.ts

import { FormContextState } from "../components/forms/formTypes";

export interface RecordCreationResults {
    wasSuccessful: boolean;
    hadErrors: boolean;
    recordsCreated: number;
    newRecordIDs: string[];
    errors?: string[]; // Optional - populated if hadErrors = true
  }
  

// --------------------- User Security Types ---------------------
export interface User {
    userID: string;
    tenantID: string;

    username: string;
    passwordHash: string;
    email: string;
    fullName?: string;

    groupID?: string;
    active: boolean;
    resetToken?: string;
    resetDate?: string;

    phoneNumber?: string;
    cellNumber?: string;

    companyName: string;
    resetPasswordDate?: string;
    online: boolean;
    dateLastOnline?: string;

    referralCode?: string;
    acceptedTerms: boolean;
    currentIP?: string;
    forceLogoff: boolean;

    location?: string;
    cellProvider?: number;
    timeZoneID?: number;

    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;

    deleted: boolean;
    deletedBy?: string;
    dateDeleted?: string;
}

export interface UserCreationRequest {
    tenantID: string;
    username: string;
    password: string;
    email: string;
    fullName?: string;
    phoneNumber?: string;
    cellNumber?: string;
    location?: string;
    cellProvider?: number;
    timeZoneID?: number;
  }

  

export interface Group {
    groupID: string;
    tenantID: string;

    groupName: string;
    description?: string;

    canCreateObjects: boolean;
    canModifyObjects: boolean;
    canDeleteObjects: boolean;

    createdBy?: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;

    deleted: boolean;
    deletedBy?: string;
    dateDeleted?: string;

    rowVersion?: string;
}

export interface GroupCreationRequest {
    tenantID: string;
    groupName: string;
    description?: string;
  }

  

export interface Team {
    teamID: string;
    tenantID: string;

    teamName: string;
    description?: string;

    createdAt: string;
    updatedAt?: string;
}

export interface TeamCreationRequest {
    tenantID: string;
    teamName: string;
    description?: string;
  }

  


/** Field Type */
export type TableField = {
    tableFieldID: string;
    fieldID: string;    
    tableID: string;   

    fieldName: string;
    dataType: string;
    displayOrder: number;

    length?: number;
    format?: string;
    inputMask?: string;

    min?: number;
    max?: number;

    allowSpecialCharacters?: boolean;
    isEncrypted?: boolean;
    isBlob?: boolean;

    required: boolean;
    readOnly: boolean;

    valueTable: string;
    listValues?: string;
    displayField?: string;


    lookupTable?: string;
    lookupField?: string;
    maxChoices?: number;
    defaultValue?: string;


    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;

    deleted?: boolean;
    deletedBy?: string;
    dateDeleted?: string;
};


export type PermissionRow = {
    id: number | string;
    group: string;
    canAdd: boolean;
    canEdit: boolean;
    canView: boolean;
    canDelete: boolean;

    // Optional metadata
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
};


export interface PageDefinition {
    id: number;
    name: string;
    type: string;
    system: boolean;
}

export interface ConfirmDialogProps {
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}

/** Table Type */
export interface Table {
    tableID: string;
    tableName: string;
    description?: string;
    isLocked?: boolean;
    published?: boolean;
    fields: TableField[];
    pages: TablePage[];
    relationships: TableRelationship[];

    permissions?: PermissionRow[];
}


/** Represents a Page tied to a Table */
export interface TablePage {
    pageID: string;
    tableID: string;
    pageType: string;       // ADD, EDIT, VIEW, POPUP-ADD, etc.
    pageName: string;
    layout?: string;
    version: number;
    isPublished: boolean;

    checkedOutBy?: string;
    checkedOutAt?: string;

    publishedBy?: string;
    publishedAt?: string;

    createdBy?: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;

    deleted: boolean;
    deletedBy?: string;
    dateDeleted?: string;

    rowVersion?: string;
}


/**
 * The root layout structure for a custom page/form.
 */
export interface PageLayout {
    css?: string;                // Optional custom CSS
    javascript?: string;         // Optional custom JavaScript
    root: PanelBlock;            // Root panel for the page layout
}


// Type Definitions
export interface PageField {
    fieldID: string;
    label: string;
    fieldName: string;
    field?: string;
    type: string;
    required?: boolean;
    maxLength?: number;
    min?: number;
    max?: number;
    readOnly?: boolean;
    inputMask?: string;
    displayMask?: string;
    cssClass?: string;
    elementId?: string;
    dataset?: string;
    radioGroup?: string;
    column?: number;
    row?: number;
    relationshipID?: string;
    selectedControl?: PageField | Control | Panel | RelatedTableControl | null;
    columns?: number;

}

export interface Control {
    id: string;
    name?: string;
    fieldID?: string;
    type: "LABEL" | "LINE" | "PANEL" | "TEXTBOX" | "CHECKBOX" | "RADIO" | "LISTBOX" | "COMBOBOX" | "CUSTOM_HTML" | "RELATED_TABLE";
    label?: string;
    html?: string;
    field?: string;
    fieldName?: string;
    column: number;
    row?: number;
    columns?: number;
    rows?: number;
    relationshipID?: string;
    selectedControl?: PageField | Control | Panel | RelatedTableControl | null;

}

export interface TableRecordDTO
{
    recordID: string;
    tableID: string;
    fieldValues: Record<string, string>;
    tenantID: string;
    createdBy: string;
    updateBy: string;
}

export interface RelatedTableControl {
    id: string;
    type: "RELATED_TABLE";
    label: string;
    name?: string;
    relationshipId: string;     // links to TableRelationship.relationshipID
    column: number;
    row?: number;
    field?: string;

    fixedFilter?: string;
    fieldLinks?: Array<{ sourceField: string; targetField: string }>;
    displayFields?: string[]; // field names or IDs
    viewPageId?: string;
    editPageId?: string;
    addPageId?: string;
    fieldID?: string;
    fieldName?: string;
    allowView?: boolean;
    allowAdd?: boolean;
    allowInlineAdd?: boolean;
    allowEdit?: boolean;
    allowInlineEdit?: boolean;
    allowDelete?: boolean;
    selectedControl?: PageField | Control | Panel | RelatedTableControl | null;
    rows?: number;
}


export interface Panel {
    id: string;
    panelId: string;
    type?: string;
    name?: string;
    label?: string;
    panelPath?: number[];
    cssClass?: string;
    columns: number;
    column?: number;
    field?: string;
    row: number;
    rows: number;
    relationshipID?: string;
    controls: Array<{ id: string; field?: PageField | Control | Panel | RelatedTableControl; column: number; row:number }>;
    selectedControl?: PageField | Control | Panel | RelatedTableControl | null;
    fieldID?: string;
    fieldName?: string;
}


/**
 * Represents a reusable panel or layout block.
 * Can contain child controls, fields, or nested panels.
 */
export interface PanelBlock {
    id: string;                  // Unique panel ID
    name?: string;               // Optional reference name (used in scripts)
    cssClass?: string;           // Optional CSS class to apply
    columns: number;             // Number of columns in the layout (1–4)
    rows: PanelRow[];           // List of rows inside this panel
    helpText?: string;
    type?: string;
}

/**
 * A row within a panel block. Contains multiple columns.
 */
export interface PanelRow {
    columns: PanelColumn[];      // List of columns for this row
}

export const ItemTypes = {
    FIELD: "FIELD",
    CONTROL: "CONTROL",
    RELATED: "RELATED_TABLE"
};


export const filterTypeMap: Record<string, number> = {
    Equals: 0,
    NotEquals: 1,
    Contains: 2,
    StartsWith: 3,
    EndsWith: 4,
    GreaterThan: 5,
    LessThan: 6,
    Between: 7,
    In: 8,
    NotIn: 9,
    Empty: 10,
    NotEmpty: 11
  };
  
  

export function isControl(ctrl: any): ctrl is Control {
    return ctrl && typeof ctrl === 'object' && 'type' in ctrl;
}

export function isField(ctrl: any): ctrl is PageField {
    if (ctrl)
    {
        if ('fieldName' in ctrl)
        {
            if('fieldID' in ctrl)
            {
                if(ctrl.fieldID!=null && ctrl.fieldID.length>0) return true;
            }
        }
        
    }
    return false;
}


/**
 * A column inside a panel row.
 * Can contain a control, field, or nested panel.
 */
export interface PanelColumn {
    content?: ControlElement | FieldElement | PanelBlock | TableRelationship | null; // The thing that goes in this column
}

/**
 * A standard control element like label, textbox, checkbox, etc.
 */
export interface ControlElement {
    id: string;
    type:
    | "LABEL"
    | "TEXTBOX"
    | "CHECKBOX"
    | "RADIO"
    | "LINE"
    | "LISTBOX"
    | "COMBOBOX"
    | "CUSTOM_HTML";
    label?: string;             // Display label
    name?: string;              // Logical name used in JS
    cssClass?: string;          // Optional styling
    readOnly?: boolean;
    required?: boolean;
    displayMask?: string;       // Optional mask (e.g., ***-**-0000)
    html?: string;              // For CUSTOM_HTML
}

/**
 * A bound field from the table schema (e.g., FirstName, Email).
 */
export interface FieldElement {
    id: string;
    fieldId: string;            // The field ID from your database
    name?: string;              // Optional override name
    cssClass?: string;
    readOnly?: boolean;
    required?: boolean;
    displayMask?: string;
}

/** Represents a Relationship from this table to another table */
export interface TableRelationship {
    relationshipID: string;
    parentTableID: string;
    childTableID: string;
    relationshipType: string; // ONE_TO_MANY, MANY_TO_MANY, etc.

    foreignKeyField?: string;
    linkedField?: string;

    viewPageID?: string;
    editPageID?: string;
    addPageID?: string;

    allowPopupEdit: boolean;
    allowPopupView: boolean;
    allowPopupAdd: boolean;

    fixedFilters?: string;

    createdBy?: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;

    deleted: boolean;
    deletedBy?: string;
    dateDeleted?: string;

    rowVersion?: string;
}

// src/types/interfaces.ts

export interface AutoFillRow {
    id: string; // GUID
    sourceType: "Dataset" | "Table";
    keyField: string;

    // One of these two is used based on the sourceType
    dataSet?: string;       // Used if sourceType === "Dataset"
    sourceTable?: string;   // Used if sourceType === "Table"

    linkedField: string;    // Field from source to be auto-filled
    enabled: boolean;       // Whether this rule is currently active
}


/** Report Data Type */
export interface ReportData {
    name: string;
    description: string;
    active: boolean;
    teams: string[];
    tables: Table[];
    layout: string;
    schedule: ScheduleEntry[];
}

export interface ScheduleEntry {
    id: string; // Unique ID for this schedule
    type: 'Manual' | 'Daily' | 'Weekly' | 'Monthly' | 'Cron';
    time?: string; // e.g., "14:00"
    dayOfWeek?: 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
    dayOfMonth?: number; // For monthly schedules
    cronExpression?: string; // For advanced Cron type
    timezone?: string; // Optional timezone identifier (e.g. "America/New_York")
    enabled: boolean;
    startDate?: string; // Optional ISO string
    endDate?: string;   // Optional ISO string
}


/** Table Selection Props */
export interface TableSelectionProps {
    reportData: ReportData;
    setReportData: React.Dispatch<React.SetStateAction<ReportData>>;
    onNext: () => void;
}

/** Report Editor Props */
export interface ReportEditorProps {
    reportData: ReportData;
    setReportData: React.Dispatch<React.SetStateAction<ReportData>>;
}

export type StepType = "IF" | "ACTION" | "EMAIL";

// Define the structure of an automation rule
export interface Automation {
    automationID: string;
    tableId?: string;
    name: string;
    triggerEvent?: "INSERT" | "UPDATE" | "DELETE" | "MANUAL";
    enabled: boolean;
    steps?: AutomationStep[];
}


export type AutomationExecution = {
    logID: string;
    startTime: string;
    endTime?: string;
    status: string;
    automation: {
        automationID: string;
        name: string;
        priority: number;
        currentRetries: number;
        maxRetries: number;
    };
};

export interface AutomationStep {
    stepID: string;
    stepType: StepType;
    tableId?: string;
    configJSON: string;
    automationID: string;
}

export interface ScheduledAutomationProps {
    automation: {
        automationID?: string;
        name: string;
        scheduleType: string;
        cronExpression?: string;
        steps: AutomationStep[];
    };
    onSave: () => void;
}

export interface AutomationEditorProps {
    automation: {
        automationID: any;
        tableId?: string;
        name: string;
        triggerEvent?: "INSERT" | "UPDATE" | "DELETE" | "MANUAL";
        steps?: AutomationStep[];
    };
    onSave: () => void;
    onClose: () => void;
}


export type AutomationLog = {
    logID: string;
    automation: {
        name: string;
        automationID: string;
    };
    startTime: string;
    endTime?: string;
    status: string;
    message: string;
};

export type ExecutionStep = {
    automationID: string;
    StepID: string;
    Order: number;
    StepType: string;
    ExecutionTime: string;
    Status: string;
    ErrorMessage?: string;
};

// Approval Workflow
export interface ApprovalWorkflow {
    workflowID: string;
    name: string;
    tableID: string;
    isEnabled: boolean;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
    deleted?: boolean;
    deletedBy?: string;
    dateDeleted?: string;
    queues?: ApprovalQueue[]; // Optional: linked queue steps
}

// Approval Queue
export interface ApprovalQueue {
    queueID: string;
    workflowID: string;
    name: string;
    order: number;
    assignmentMode: "ROUND_ROBIN" | "MANUAL" | "RULE_BASED";
    isFinalStep: boolean;
    actions?: ApprovalAction[]; // Optional: approval actions
    assignments?: ApprovalQueueAssignment[]; // Optional: assigned users/teams
}

// Assignment of user/group/team to a queue
export interface ApprovalQueueAssignment {
    recordID: string;
    recordTitle: string;
    queueName: string;
    status: string;
    priority: string;
    flagColor?: string;
    assignedAt?: string;
    assignmentID: string;
    queueID: string;
    userID?: string;
    groupID?: string;
    teamID?: string;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
    deleted?: boolean;
    deletedBy?: string;
    dateDeleted?: string;
}

// Approval Action associated with a queue
export interface ApprovalAction {
    actionID: string;
    queueID: string;
    label: string;
    actionType: "ROUTE_TO_QUEUE" | "TRIGGER_AUTOMATION" | "COMPLETE" | string;
    targetQueueID?: string;
    automationID?: string;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
    deleted?: boolean;
    deletedBy?: string;
    dateDeleted?: string;
}

// ============================
// RULE TYPES
// ============================

/**
 * Supported rule behaviors.
 * These define what the rule is *doing* when it's evaluated.
 */
export type RuleType =
    | "show"        // Makes a field or panel visible
    | "hide"        // Hides a field or panel
    | "enable"      // Makes a field editable
    | "disable"     // Makes a field read-only
    | "set"         // Sets a static value on a field
    | "calculate"   // Dynamically calculates a value
    | "validate"    // Applies custom validation and error messages
    | "trigger"     // Fires a custom event (e.g. a form hook)
    | "navigate";   // Navigates to another page, tab, etc.

/**
 * Represents a single dynamic rule for a field, control, or layout element.
 * These are usually evaluated on form load, on change, or on trigger.
 */
export interface Rule {
    ruleID?: string;
    type: RuleType;
    target: string;

    condition?: string;
    value?: any;
    meta?: Record<string, any>;

    description?: string;
    triggerOn?: "load" | "change" | "manual";
    priority?: number;
    sourceField?: string;

    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;

    deleted?: boolean;
    deletedBy?: string;
    dateDeleted?: string;
}



export interface ParsedFormLayout {
    rootPanels: PanelBlock[];
    customJS?: string;
    customCSS?: string;
}

// Freeze methods at definition
export class JSFormBridge {
    constructor(private readonly context: FormContextState) {
        Object.freeze(this);
    }

    get = (fieldId: string) => this.context.getValue(fieldId);
    set = (fieldId: string, value: any) => this.context.setValue(fieldId, value);
    isDirty = (fieldId: string) => !!this.context.dirty[fieldId];
    getAll = () => ({ ...this.context.data });
    reset = () => this.context.reset();
    show = (fieldId: string) => console.debug(`[bridge.show] ${fieldId} (stub)`);
    hide = (fieldId: string) => console.debug(`[bridge.hide] ${fieldId} (stub)`);
}
Object.freeze(JSFormBridge.prototype);

export interface EventContext {
    pageId?: string;
    tableId?: string;
    mode?: "ADD" | "EDIT" | "VIEW";
    recordData?: Record<string, any>;
    formData?: Record<string, any>; // Entire form snapshot
    fieldId?: string;
    value?: any;
    user?: {
        id?: string;
        name?: string;
        email?: string;
        roles?: string[];
        groups?: string[];
    };
    [key: string]: any; // ← allow custom keys like page, etc.
}

export type FormEvent =
    | "OnLoad"
    | "OnChange"
    | "OnExit"
    | "OnEnterPage"
    | "OnExitPage"
    | "OnSubmit"
    | "OnFocus"
    | "OnBlur"
    | string; // Custom or future event types


export const FORM_EVENT_TARGET = "FORM";
export const PAGE_EVENT_TARGET = "PAGE";
export const SYSTEM_EVENT_TARGET = "SYSTEM";

// ============================
// Tenant Flag Rules
// ============================

export interface TenantFlagRule {
    id: string;                         // Unique identifier
    tenantId: string;                   // Tenant owning the rule
    ruleOrder: number;                  // Determines evaluation order
    ruleName: string;                   // Friendly name for UI
    fieldName: string;                  // Table field to evaluate
    comparisonOperator: string;        // e.g., '==', '!=', '>', '<=', 'contains'
    comparisonValue?: string | null;   // Right-hand value to compare against
    matchCreatedByMe: boolean;         // Whether to match only records created by the current user
    matchCreatedBefore?: string | null;// ISO date string
    rowColor?: string | null;          // Hex or named CSS color
    iconName?: string | null;          // Icon identifier for rendering
    tooltipText?: string | null;       // Optional descriptive text for hovering
    isEnabled: boolean;                // Whether this rule is active
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
}

export interface CreateTenantFlagRuleRequest {
    tenantId: string;
    ruleName: string;
    fieldName: string;
    comparisonOperator: string;
    comparisonValue?: string | null;
    matchCreatedByMe: boolean;
    matchCreatedBefore?: string | null;
    rowColor?: string | null;
    iconName?: string | null;
    tooltipText?: string | null;
}

export interface UpdateTenantFlagRuleRequest extends CreateTenantFlagRuleRequest {
    id: string;
    isEnabled: boolean;
}

export interface ReorderTenantFlagRulesRequest {
    tenantId: string;
    rules: {
        ruleId: string;
        ruleOrder: number;
    }[];
}
